/**
 * Driver tab — the per-car pane of the right panel (mockup g-detail-h +
 * tpl-drv). Rendered in DETAIL mode (media slot = live video) and in
 * THEATER mode (media slot = mini-map; the big video owns the stage).
 *
 * Carries over every load-bearing piece of the v1 DetailPanel:
 * stage-progress chip, Compare flow (strip + keyed comparee player),
 * lap selection (now via LapSelector), and the telemetry cells —
 * extended with the stream-health Lag cell.
 */

import React from 'react';
import { Star, X } from 'lucide-react';

import { cn, displayVehicle } from '@/lib/utils';
import { Lap } from '../../services/fleet';
import { GpsSummary } from '../../types';
import { SpeedChart } from '../../plugins/synced_viewer/SpeedChart';
import { Scrubber } from './Scrubber';
import { EventLiveDevice } from '../../hooks/useEventLiveDevices';
import { LG_MEDIA_QUERY, useMediaQuery } from '../../hooks/useMediaQuery';
import { displayCarNumber } from './carNumber';
import { HLSPlayerHandle } from '../HLSPlayer';
import FeedPlayer from './FeedPlayer';
import LapSelector, { formatLapTime, formatSigma, sigmaToneClass } from './LapSelector';
import SessionSelector, { DriverSessionRow } from './SessionSelector';
import StageEventsSection, { StageEventEntry } from './StageEvents';
import { LagInfo, SEVERITY_TEXT } from './lag';

export interface CompareState {
  runId: string;
  sessionId: string;
  seedUtcMs: number;
}

const TeleCell: React.FC<{
  label: string;
  children: React.ReactNode;
  tone?: string;
  title?: string;
}> = ({ label, children, tone, title }) => (
  <div className="rounded-sm bg-bg-surface px-2.5 py-2" title={title}>
    <div className="text-[9px] font-semibold uppercase tracking-wide text-fg-muted">{label}</div>
    <div className={cn('mt-0.5 font-mono text-[15px] font-bold text-fg', tone)}>{children}</div>
  </div>
);

const StageProgressChip: React.FC<{
  progress: { pct: number; sM: number };
  totalLengthM: number;
}> = ({ progress, totalLengthM }) => {
  const offStage = progress.pct < 0;
  const pct = offStage ? 0 : Math.min(100, Math.max(0, progress.pct));
  return (
    <div className="border-b border-border-subtle bg-bg-elevated px-4 py-2">
      <div className="mb-1 flex items-center justify-between text-[10px] uppercase tracking-wide">
        <span className="text-fg-secondary">Stage progress</span>
        <span className={offStage ? 'text-fg-muted' : 'text-fg'}>
          {offStage
            ? 'off-stage'
            : `${pct.toFixed(0)}% · ${(progress.sM / 1000).toFixed(2)} / ${(totalLengthM / 1000).toFixed(2)} km`}
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-bg-base">
        <div
          className={`h-full ${offStage ? 'bg-fg-muted/40' : 'bg-accent'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

export const DriverTab: React.FC<{
  device: EventLiveDevice;
  replay?: boolean;
  /** Speed (km/h) at the current playhead — replay-aware (same source as
   *  the glass rail). Null = no reading. Falls back to live device speed
   *  only when live. */
  playbackSpeedKmh?: number | null;
  onClose: () => void;
  /** Media slot content — FeedPlayer (detail) or MiniMap (theater). */
  media: React.ReactNode;
  /** Stream lag of the actively-playing camera (source: video end vs
   *  newest GPS fix). Null when no player is live for this car. */
  playerLag: LagInfo | null;
  stageProgress: { pct: number; sM: number } | null;
  stageLengthM: number | null;
  compareCapable: boolean;
  compareBusy: boolean;
  onCompareOpen: () => void;
  compareRun: CompareState | null;
  compareePlayerRef: React.MutableRefObject<HLSPlayerHandle | null>;
  /** Follow state for this device + toggle (orchestrator-owned). */
  followed?: boolean;
  onToggleFollow?: (id: string) => void;
  onCompareClose: () => void;
  /** Circuit-only lap selector inputs. */
  isCircuit: boolean;
  laps: Lap[];
  selectedLapIdx: number | null;
  onPickLap: (lap: Lap, idx: number) => void;
  onPickLive: () => void;
  stageEvents: StageEventEntry[];
  /** Session surfacer: this car's recordings (newest first, stage-
   *  tagged) + pick handler + the mounted session for row highlight. */
  sessions: DriverSessionRow[];
  onPickSession: (sessionId: string) => void;
  mountedSessionId: string | null;
  /** Playhead-synced speed chart inputs (public viewer). Optional so any
   *  caller that doesn't pass a trail simply renders no chart. */
  trailTrack?: GpsSummary['track'] | null;
  /** Video-coverage intervals (UTC ms) for the loaded-edge mark + scrubber fill. */
  trailCoverage?: Array<[number, number]> | null;
  playheadUtc?: number | null;
  onSeek?: (utcMs: number) => void;
  /** Mounted primary player's first PDT (v2 only) — clamps the Speed
   *  chart's left edge to where video actually starts. Null = today's
   *  behavior (chart domain starts at the earliest GPS sample). */
  videoStartMs?: number | null;
}> = ({
  device,
  replay = false,
  playbackSpeedKmh = null,
  trailTrack = null,
  trailCoverage = null,
  playheadUtc = null,
  onSeek,
  videoStartMs = null,
  onClose,
  media,
  playerLag,
  stageProgress,
  stageLengthM,
  compareCapable,
  compareBusy,
  onCompareOpen,
  compareRun,
  compareePlayerRef,
  followed = false,
  onToggleFollow,
  onCompareClose,
  isCircuit,
  laps,
  selectedLapIdx,
  onPickLap,
  onPickLive,
  stageEvents,
  sessions,
  onPickSession,
  mountedSessionId,
}) => {
  const isLive = !replay && device.session_id != null;
  // The single σ marker for the telemetry grid (field decision: per-chip
  // ±σ overflowed the LapSelector cells). Follows the SELECTED lap; on
  // the live feed it shows the most recent completed lap's σ.
  const timingIdx =
    isCircuit && laps.length > 0
      ? selectedLapIdx != null
        ? selectedLapIdx
        : laps.length - 1
      : null;
  const timingLap =
    timingIdx != null && laps[timingIdx]?.sigma_ms != null ? laps[timingIdx] : null;
  // Driver header. Mobile (handover §3.6 Watch tab) puts the media
  // ABOVE this row and the bottom tab bar owns navigation, so the X
  // is desktop-only. Ordering is decided in JS (not CSS `order`) so
  // keyboard/screen-reader order always matches the visual order.
  const isDesktop = useMediaQuery(LG_MEDIA_QUERY);
  const driverHeader = (
    <div className="flex shrink-0 items-center gap-2.5 border-b border-border-subtle px-3.5 py-2.5">
      <div className="font-mono text-[22px] font-bold leading-none text-accent">
        {displayCarNumber(device)}
      </div>
      <div className="min-w-0 flex-1">
        {/* The pane's focal element — everything else in the rail sits
            at 9–11px micro scale, so the crew name anchors the eye. */}
        <div className="truncate text-[16px] font-bold leading-tight text-fg">
          {device.driver_name || device.device_id}
        </div>
        <div className="mt-px truncate text-[10px] text-fg-muted">
          {/* displayVehicle: "." is the operator's intentional-blank
              convention; no em-dash placeholder either — "— · idle"
              reads as a rendering bug, not an empty field. */}
          {[displayVehicle(device.vehicle), replay ? 'replay' : isLive ? 'recording' : 'idle']
            .filter(Boolean)
            .join(' · ')}
        </div>
      </div>
      <button
        type="button"
        onClick={() => onToggleFollow?.(device.device_id)}
        title={followed ? 'Unfollow — drops the map ping + auto-pan' : 'Follow — ping ring on the map, auto-pan while selected'}
        className={
          followed
            ? 'inline-flex items-center gap-1 rounded-sm border border-accent bg-accent px-2.5 py-1.5 text-[11px] font-bold text-fg-inverse'
            : 'inline-flex items-center gap-1 rounded-sm border border-[var(--accent-primary-br,rgba(245,166,35,.3))] bg-[var(--accent-primary-bg)] px-2.5 py-1.5 text-[11px] font-semibold text-accent hover:border-accent'
        }
      >
        <Star className="size-3.5" />
        {followed ? 'Following' : 'Follow'}
      </button>
      {isDesktop && (
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 text-fg-secondary hover:bg-bg-elevated hover:text-fg"
          aria-label="Close · back to map"
        >
          <X className="size-4" />
        </button>
      )}
    </div>
  );
  // Media slot — video in detail mode, mini-map in theater. Pinned in
  // both layouts; sections scroll below. The max-w cap keeps a 16:9
  // video short enough on landscape phones that the pinned header +
  // scroll area stay reachable.
  const mediaSlot = (
    <div className="w-full shrink-0 bg-bg-video">
      <div className="mx-auto w-full max-w-[calc((100dvh-180px)*1.7778)] lg:max-w-none">
        {media}
      </div>
    </div>
  );
  return (
    <>
      {isDesktop ? (
        <>
          {driverHeader}
          {mediaSlot}
        </>
      ) : (
        <>
          {mediaSlot}
          {driverHeader}
        </>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto">
        {stageProgress && stageLengthM != null && (
          <StageProgressChip progress={stageProgress} totalLengthM={stageLengthM} />
        )}

        {(compareCapable || compareRun) && (
          <div className="flex items-center gap-2 border-b border-border-subtle bg-bg-elevated px-3 py-2 text-xs">
            {compareRun ? (
              <>
                <span className="font-mono text-fg-secondary">comparing</span>
                <button
                  type="button"
                  onClick={onCompareClose}
                  className="ml-auto rounded border border-border-subtle bg-bg-surface px-2 py-1 text-fg hover:bg-bg-base"
                >
                  End compare
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={onCompareOpen}
                disabled={compareBusy || !compareCapable}
                className="rounded border border-border-subtle bg-bg-surface px-2 py-1 text-fg hover:border-accent hover:text-accent disabled:opacity-50"
              >
                {compareBusy ? 'Loading…' : 'Compare with another vehicle'}
              </button>
            )}
          </div>
        )}
        {compareRun && (
          // M21: keyed on session_id — never reuse a player across sessions.
          <FeedPlayer
            key={compareRun.sessionId}
            sessionId={compareRun.sessionId}
            playerRef={compareePlayerRef}
            initialSeekUtcMs={compareRun.seedUtcMs}
          />
        )}

        {/* Lap selector — circuit events only (handover §3) */}
        {isCircuit && (
          <LapSelector
            laps={laps}
            live={isLive}
            selectedIdx={selectedLapIdx}
            onPickLap={onPickLap}
            onPickLive={onPickLive}
          />
        )}

        {/* Stage time — point-to-point (rally / non-circuit) events. One
            elapsed time per run (launch → finish), computed coarsely at
            finish; not a lap grid. */}
        {!isCircuit && (
          <div className="border-b border-border-subtle p-3.5">
            <div className="mb-2.5 text-[10px] font-bold uppercase tracking-wider text-fg-muted">
              Stage time
            </div>
            {laps.length > 0 ? (
              <div className="flex items-baseline gap-2">
                {/* Hero number — the one stat a rally fan came for. */}
                <span className="font-mono text-[28px] font-bold tracking-tight text-fg">
                  {formatLapTime(laps[laps.length - 1].duration_ms)}
                </span>
                <span className="font-mono text-[10px] text-fg-muted">
                  {laps[laps.length - 1].method ?? 'coarse'}
                </span>
              </div>
            ) : (
              <div className="py-1 font-mono text-[10px] text-fg-muted">
                {isLive ? 'timing in progress — computed at finish' : 'no stage time yet'}
              </div>
            )}
          </div>
        )}

        {/* Session surfacer — every recording of this car, stage-tagged;
            tap to play (LIVE row returns to the live feed). */}
        <SessionSelector
          rows={sessions}
          mountedSessionId={mountedSessionId}
          onPick={onPickSession}
        />

        {/* Live telemetry */}
        <div className="border-b border-border-subtle p-3.5">
          <div className="mb-2.5 flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-fg-muted">
            <span>{replay ? 'Telemetry' : 'Live telemetry'}</span>
            {isLive && (
              <span className="font-mono font-medium normal-case tracking-normal text-status-online">
                ● updating
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4">
            <TeleCell label="Speed">
              {playbackSpeedKmh != null ? (
                <>
                  {playbackSpeedKmh.toFixed(1)}
                  <span className="ml-1 text-[10px] font-normal text-fg-secondary">km/h</span>
                </>
              ) : (
                '—'
              )}
            </TeleCell>
            {/* Lag source: video end vs newest GPS fix when a
                live player is mounted for this car; "—" otherwise. */}
            <TeleCell label="Lag" tone={playerLag ? SEVERITY_TEXT[playerLag.severity] : undefined}>
              {playerLag ? playerLag.label : '—'}
            </TeleCell>
            <TeleCell label="Battery">
              {device.battery_level != null
                ? `${Math.round(device.battery_level * 100)}%`
                : '—'}
            </TeleCell>
            <TeleCell label="Heading">
              {device.heading_deg != null ? `${Math.round(device.heading_deg)}°` : '—'}
            </TeleCell>
            {isCircuit && (
              <TeleCell
                label="Timing"
                tone={timingLap ? sigmaToneClass(timingLap.confidence) : undefined}
                title={
                  timingLap
                    ? `GPS timing 1σ uncertainty of L${timingIdx! + 1}${timingLap.method ? ` (${timingLap.method})` : ''}`
                    : 'GPS timing uncertainty — appears once a lap is refined'
                }
              >
                {timingLap ? (
                  <>
                    {formatSigma(timingLap.sigma_ms!)}
                    <span className="ml-1 text-[10px] font-normal text-fg-secondary">
                      L{timingIdx! + 1}
                    </span>
                  </>
                ) : (
                  '—'
                )}
              </TeleCell>
            )}
          </div>
        </div>

        {/* Playhead-synced speed chart. Reuses the synced_viewer SpeedChart
            (recharts ReferenceLine at the playhead + click-to-seek). Only
            renders when a GPS trail exists for this car. */}
        {trailTrack && trailTrack.length > 0 && (
          <div className="border-b border-border-subtle py-3">
            {/* Label stays padded; the chart bleeds nearly edge-to-edge
                (px-0.5) so it uses the full panel width + scales better. */}
            <div className="mb-2 px-3.5 text-[10px] font-bold uppercase tracking-wider text-fg-muted">
              Speed
            </div>
            <div className="h-24 px-0.5">
              <SpeedChart
                summary={{ track: trailTrack } as GpsSummary}
                currentUtcMs={playheadUtc}
                onSeek={onSeek}
                unit="kmh"
                domainStartMs={videoStartMs}
                compact
                loadedEdgeUtc={
                  trailCoverage && trailCoverage.length
                    ? Math.max(...trailCoverage.map(([, e]) => e))
                    : null
                }
              />
            </div>
            {/* Timeline scrubber — coverage fill + playhead + live edge. */}
            <div className="mt-2 px-3.5">
              <Scrubber
                startUtc={trailTrack[0][2]}
                endUtc={trailTrack[trailTrack.length - 1][2]}
                playheadUtc={playheadUtc}
                coverage={trailCoverage}
                onSeek={onSeek}
              />
            </div>
          </div>
        )}

        {/* Stage events — only what the SSE stream already carries */}
        <StageEventsSection entries={stageEvents} />
      </div>
    </>
  );
};

export default DriverTab;
